import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import ProgressBar from "../helpers/progress_bar"
import ContactDrawer, { fetchJson } from "../helpers/contact_drawer"

/* global AbortController */

// Still needed to namespace the jQuery-plugin event bindings that stay on jQuery (select2,
// daterangepicker, DataTables draw.dt) - native listeners use the shared AbortController instead.
const NS = ".contacts";

// Markup for the select-all dropdown injected into the table header on draw. Duplicated in
// order_recipients_controller.js, which renders the same dropdown.
//
// A hardcoded literal with no interpolation, no user data and no <script> tag - safe to inject
// directly (S4: not the same as the drawer's server-response HTML below).
const SELECT_ALL_DROPDOWN_HTML = `
  <div class="dropdown select-all btn-group">
    <button id="select-all-dropdown" class="btn-dropdown btn btn-borderless btn-transparent dropdown-toggle btn-sm" data-toggle="dropdown" type="button">
      <div class="form-group top-level">
        <input class="select-all" name="select-all" type="checkbox" aria-label="Select multiple elements" data-contacts-target="bulkSelectCheckbox">
        <span class="badge">(0)</span>
        <span class="caret"></span>
      </div>
    </button>
    <ul class="dropdown-menu">
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-all-elements" class="boolean optional" type="checkbox" aria-label="Select all elements">
            <label class="boolean optional" for="select-all-elements">All Recipients</label>
          </div>
        </div>
      </li>
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-this-page" class="boolean optional" type="checkbox" aria-label="Select this page">
            <label class="boolean optional" for="select-this-page">This Page</label>
          </div>
        </div>
      </li>
    </ul>
  </div>
`;

// Paired with the shared `datatable` controller on the same `.content-box` mount: that one owns
// the DataTables lifecycle, this one owns the drawer, the filters, the date range picker, the
// bulk-selection bookkeeping and the bulk actions.
//
// Mounts on both subdomain/contacts/index.html.haml (the full page) and
// subdomain/contacts/import.html.haml (only the `#contact_import_contact_list_id` select2). Every
// target is `hasXTarget`-guarded so the selectors that find nothing on import simply no-op.
//
// Several lookups below are deliberately global rather than scoped to `this.element`, because
// their target renders as a SIBLING of `.content-box`: `#contacts-drawer`, `#contact-lists-modal`,
// `#contact-assignment-modal` and `#assign-multiple-form`. The `.view-/.edit-/.delete-contact-link`
// delegations bind from `document` for the same reason - the drawer renders its own copies of
// those links outside `.content-box`. Scoping any of these would silently break them.
export default class extends Controller {
  static get targets() {
    return ["table", "eventFilter", "contactListFilter", "dateRangeInput", "buttonFooter", "contactListSelect", "bulkSelectCheckbox"];
  }

  connect() {
    this.sendToIds = new Set();
    this.exceptIds = new Set();
    this.allCheckboxChecked = false;
    this.pageCheckboxCheckedNum = null;
    // Replaces the jQuery `NS`-namespace cleanup for every native listener bound below; jQuery
    // plugin bindings (select2, daterangepicker, draw.dt) still need their own `.off(NS)`.
    this.abort = new AbortController();

    this.contactDrawer = new ContactDrawer(document.getElementById("contacts-drawer"), { signal: this.abort.signal });

    if (this.hasTableTarget) {
      // R1 (skip): `data-source` is a plain, un-namespaced attribute - not this controller's own
      // Values API territory. It's the same convention 15+ other datatable-driving controllers
      // use verbatim (campaigns, orders, gift_events, automations, subscription_events, ...), and
      // some of their specs assert the literal attribute name directly
      // (`have_css("...[data-source=...]")`, `evaluate_script(...).getAttribute("data-source")`).
      // Moving just this controller off it would be inconsistent with the rest of the app for no
      // decoupling benefit. See patterns.md.
      this.initialSource = this.tableTarget.getAttribute("data-source");

      this.onDraw = this.onDraw.bind(this);
      // R3 (skip): draw.dt is announced by the DataTables plugin through jQuery - a data-action
      // cannot subscribe to it, so this stays a manual jQuery binding.
      $(this.tableTarget).on(`draw.dt${NS}`, this.onDraw);
    }

    if (this.hasEventFilterTarget) {
      // select2 plugin - stays jQuery.
      $(this.eventFilterTarget).select2({ placeholder: "Next event date", width: "100%", allowClear: true, minimumResultsForSearch: -1 });
      $(this.eventFilterTarget).on(`change${NS}`, () => { this.resetSelections(); this.reload(); });
    }

    this.setupContactListFilter();

    if (this.hasDateRangeInputTarget) {
      const startDate = moment().subtract(183, "days");
      const endDate = moment().add(182, "days");

      // daterangepicker plugin - stays jQuery.
      $(this.dateRangeInputTarget).daterangepicker({
        buttonClasses: "btn btn-main btn-md",
        cancelButtonClasses: "btn-ghost",
        opens: "left",
        alwaysShowCalendars: true,
        showCustomRangeLabel: false,
        minDate: startDate,
        maxDate: endDate,
      }, (start, end) => {
        const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
        this.resetSelections();
        this.reload({ range: newRange });
      });

      $(this.dateRangeInputTarget).on(`show.daterangepicker${NS}`, (ev, picker) => {
        const $clearButton = $(`<button class="btn btn-borderless clearBtn">Clear All</button>`);

        $clearButton.on(`click${NS}`, () => {
          picker.setStartDate(moment().format("MM/DD/YYYY"));
          picker.setEndDate(moment().format("MM/DD/YYYY"));
          picker.hide();

          this.resetSelections();
          this.tableTarget.setAttribute("data-source", this.initialSource);
          this.reload();
        });

        $clearButton.insertAfter($(".drp-selected"));
      });

      $(this.dateRangeInputTarget).on(`hide.daterangepicker${NS}`, () => $(".clearBtn").remove());
    }

    // Display the new contact layout
    document.getElementById("add-contact")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.contactDrawer.open(event.currentTarget.href);
    }, { signal: this.abort.signal });

    // Both links just open the drawer on the clicked row's href, so one handler covers them.
    document.addEventListener("click", (event) => {
      const link = event.target.closest(".view-contact-link, .edit-contact-link");
      if (!link) { return; }
      event.preventDefault();
      this.contactDrawer.open(link.href);
    }, { signal: this.abort.signal });

    document.addEventListener("click", (event) => {
      const link = event.target.closest(".delete-contact-link");
      if (!link) { return; }
      this.deleteContact(event, link);
    }, { signal: this.abort.signal });

    document.addEventListener("click", (event) => {
      const target = event.target.closest("#select-all-elements, #select-this-page");
      if (!target) { return; }
      this.onSelectAllOrPage(event, target);
    }, { signal: this.abort.signal });

    document.addEventListener("click", (event) => {
      const link = event.target.closest(".send-gift-contact-link");
      if (!link) { return; }
      event.preventDefault();
      link.parentElement?.querySelector(":scope > form.edit_contact")?.submit();
    }, { signal: this.abort.signal });

    document.addEventListener("change", (event) => {
      const checkbox = event.target.closest(".contact-checkbox");
      if (!checkbox) { return; }
      this.onContactCheckboxChange(event, checkbox);
    }, { signal: this.abort.signal });

    document.addEventListener("click", (event) => {
      const submit = event.target.closest("#assign-multiple-form input[type='submit']");
      if (!submit) { return; }
      this.onAssignMultipleSubmit(event);
    }, { signal: this.abort.signal });

    document.getElementById("delete-contact-submit")?.addEventListener("click", (event) => this.onDeleteSelectedSubmit(event), { signal: this.abort.signal });

    // Raised by contact_lists_controller.js after a list is created/edited/deleted through its
    // modal - a native CustomEvent broadcast on `document` since `.content-box` isn't an ancestor
    // of the modal that raises it (they're siblings), so Stimulus's element-scoped dispatch()
    // can't be used here.
    document.addEventListener("contact-lists-changed", () => this.reloadTopPanel(), { signal: this.abort.signal });

    if (this.hasButtonFooterTarget) {
      this.buttonFooterTarget.querySelector("#send-gifts-button")?.addEventListener("click", (event) => this.onSendGifts(event), { signal: this.abort.signal });
      this.buttonFooterTarget.querySelector("#delete-selected-button")?.addEventListener("click", (event) => this.onDeleteSelectedClick(event), { signal: this.abort.signal });
    }

    if (this.hasContactListSelectTarget) {
      // select2 plugin - stays jQuery.
      $(this.contactListSelectTarget).select2({ placeholder: "Select group list", width: "100%", allowClear: true, minimumResultsForSearch: -1 });
    }
  }

  disconnect() {
    this.abort.abort();

    if (this.hasTableTarget) { $(this.tableTarget).off(NS); }
    if (this.hasEventFilterTarget) { $(this.eventFilterTarget).off(NS); }
    if (this.hasContactListFilterTarget) { $(this.contactListFilterTarget).off(NS); }
    if (this.hasDateRangeInputTarget) { $(this.dateRangeInputTarget).off(NS); }
    if (this.hasContactListSelectTarget && $(this.contactListSelectTarget).data("select2")) { $(this.contactListSelectTarget).select2("destroy"); }
  }

  setupContactListFilter() {
    if (!this.hasContactListFilterTarget) { return; }

    // select2 plugin - stays jQuery.
    $(this.contactListFilterTarget).select2({ placeholder: "All Contacts", width: "100%", allowClear: false, multiple: true, minimumResultsForSearch: -1 });
    $(this.contactListFilterTarget).on(`change${NS}`, () => {
      this.resetSelections();
      this.setSelectionColorClasses();
      this.reload();
    });
  }

  setSelectionColorClasses() {
    Array.from(this.contactListFilterTarget.selectedOptions).forEach((option) => {
      const colorClass = option.dataset.color;
      // Unscoped, matching the original `$(".contact-list-container .select2")` lookup.
      document.querySelectorAll(`.contact-list-container .select2 .select2-selection__choice[title='${CSS.escape(option.textContent)}']`).forEach((el) => el.classList.add(colorClass));
    });
  }

  // The page-specific half of the draw callback; the standard reskin belongs to the shared
  // `datatable` controller. Row link clicks are delegated once in connect() rather than rebound
  // here on every draw.
  onDraw(event, settings) {
    // S1, not a surface: SELECT_ALL_DROPDOWN_HTML is the module constant above, a fixed literal
    // with nothing interpolated into it -- no partial to name and nothing from a response.
    const checkboxHeader = this.tableTarget.querySelector("thead th.checkbox");
    if (checkboxHeader) { checkboxHeader.innerHTML = SELECT_ALL_DROPDOWN_HTML; }

    this.setRecordsCount(settings.fnRecordsTotal());
    this.setPageNumber(Math.floor(settings._iDisplayStart / settings._iDisplayLength));

    const allCheckbox = document.getElementById("select-all-elements");
    const pageCheckbox = document.getElementById("select-this-page");

    if (this.allCheckboxChecked) {
      if (this.hasBulkSelectCheckboxTarget) { this.bulkSelectCheckboxTarget.checked = true; }
      if (allCheckbox) { allCheckbox.checked = true; }
    } else if (this.pageCheckboxCheckedNum !== null && this.pageCheckboxCheckedNum === this.getPageNumber()) {
      if (this.hasBulkSelectCheckboxTarget) { this.bulkSelectCheckboxTarget.checked = true; }
      if (pageCheckbox) { pageCheckbox.checked = true; }
    }

    this.tableTarget.querySelectorAll(".contact-checkbox").forEach((checkbox) => {
      const id = checkbox.dataset.id;
      if (this.allCheckboxChecked && !this.exceptIds.has(id)) {
        checkbox.checked = true;
      } else if (this.sendToIds.has(id)) {
        checkbox.checked = true;
      }
    });
  }

  setRecordsCount(count) { document.getElementById("records-count").textContent = count; }
  getRecordsCount() { return parseInt(document.getElementById("records-count").textContent); }
  setPageNumber(num) { document.getElementById("page-number").textContent = num; }
  getPageNumber() { return parseInt(document.getElementById("page-number").textContent); }

  getNumCheckedContacts() {
    if (this.allCheckboxChecked) {
      return this.getRecordsCount() - this.exceptIds.size;
    }
    return this.sendToIds.size;
  }

  resetSelections() {
    this.sendToIds.clear();
    this.exceptIds.clear();
    this.allCheckboxChecked = false;
    this.pageCheckboxCheckedNum = null;
    this.updateFooterUI();
  }

  updateFooterUI() {
    if (!this.hasButtonFooterTarget) { return; }

    if (this.allCheckboxChecked || this.sendToIds.size > 0) {
      this.buttonFooterTarget.style.display = "flex";
    } else {
      this.buttonFooterTarget.style.display = "none";
    }

    const numChecked = this.getNumCheckedContacts();
    const contactCount = this.buttonFooterTarget.querySelector("#contact-count");
    if (contactCount) { contactCount.textContent = `${numChecked} ${numChecked === 1 ? "user" : "users"}`; }
  }

  onSelectAllOrPage(event, selectedOptionCheckbox) {
    const allCheckbox = document.getElementById("select-all-elements");
    const pageCheckbox = document.getElementById("select-this-page");
    const optionChecked = selectedOptionCheckbox.checked;

    if (allCheckbox && allCheckbox.id === selectedOptionCheckbox.id) {
      this.allCheckboxChecked = allCheckbox.checked;
      if (pageCheckbox) { pageCheckbox.checked = false; }
      this.pageCheckboxCheckedNum = null;
    }

    if (pageCheckbox && pageCheckbox.id === selectedOptionCheckbox.id) {
      this.pageCheckboxCheckedNum = pageCheckbox.checked ? this.getPageNumber() : null;
      if (allCheckbox) { allCheckbox.checked = false; }
      this.allCheckboxChecked = false;
    }

    this.sendToIds.clear();
    this.exceptIds.clear();

    this.element.querySelectorAll(".contact-checkbox").forEach((element) => {
      element.checked = optionChecked;

      if (pageCheckbox && pageCheckbox.id === selectedOptionCheckbox.id) {
        const id = element.dataset.id;
        if (optionChecked) { this.sendToIds.add(id); } else { this.sendToIds.delete(id); }
      }
    });

    if (this.hasBulkSelectCheckboxTarget) { this.bulkSelectCheckboxTarget.checked = optionChecked; }
    this.updateFooterUI();
  }

  onContactCheckboxChange(event, checkbox) {
    const id = checkbox.dataset.id;

    if (this.allCheckboxChecked) {
      if (checkbox.checked) { this.exceptIds.delete(id); } else { this.exceptIds.add(id); }
    } else {
      if (checkbox.checked) { this.sendToIds.add(id); } else { this.sendToIds.delete(id); }
    }

    this.updateFooterUI();
  }

  deleteContact(event, link) {
    event.preventDefault();
    const deleteSelectedForm = document.getElementById("delete-selected-form");
    if (deleteSelectedForm) { deleteSelectedForm.action = link.href; }
    // #contact-delete-modal is a Bootstrap modal; .modal() is the plugin's own API - stays jQuery.
    $("#contact-delete-modal").modal("show");
  }

  onDeleteSelectedClick(event) {
    event.preventDefault();
    const contactUrl = event.currentTarget.dataset.url;
    const deleteSelectedForm = document.getElementById("delete-selected-form");
    if (deleteSelectedForm) { deleteSelectedForm.action = contactUrl; }
    $("#contact-delete-modal").modal("show");
  }

  onDeleteSelectedSubmit(event) {
    event.preventDefault();

    const deleteSelectedForm = document.getElementById("delete-selected-form");
    event.currentTarget.setAttribute("disabled", "disabled");

    if (this.sendToIds.size > 0) {
      deleteSelectedForm.querySelector("input[name='destroy_except_ids']")?.remove();
      const contactIdsInput = deleteSelectedForm.querySelector("input[name='destroy_contact_ids']");
      if (contactIdsInput) { contactIdsInput.value = Array.from(this.sendToIds); }
    } else {
      deleteSelectedForm.querySelector("input[name='destroy_contact_ids']")?.remove();
      const exceptIdsInput = deleteSelectedForm.querySelector("input[name='destroy_except_ids']");
      if (exceptIdsInput) { exceptIdsInput.value = Array.from(this.exceptIds); }
    }

    deleteSelectedForm.submit();
  }

  onAssignMultipleSubmit(event) {
    event.preventDefault();
    const assignMultipleForm = document.getElementById("assign-multiple-form");
    if (!assignMultipleForm) { return; }

    if (assignMultipleForm.querySelectorAll(".contact-list-checkbox:checked").length === 0) { return; }

    if (this.sendToIds.size > 0) {
      assignMultipleForm.querySelector("input[name='except_ids']")?.remove();
      const contactIdsInput = assignMultipleForm.querySelector("input[name='contact_ids']");
      if (contactIdsInput) { contactIdsInput.value = Array.from(this.sendToIds); }
    } else {
      assignMultipleForm.querySelector("input[name='contact_ids']")?.remove();
      const exceptIdsInput = assignMultipleForm.querySelector("input[name='except_ids']");
      if (exceptIdsInput) { exceptIdsInput.value = Array.from(this.exceptIds); }
    }

    // R1 (skip): per-checkbox `data-id`, read off a repeated collection - the Values API is
    // scoped to the controller element and can't express a per-element attribute (D3).
    const contactListIds = Array.from(assignMultipleForm.querySelectorAll("input.contact-list-checkbox:checked")).map((el) => el.getAttribute("data-id"));
    const contactListIdsInput = assignMultipleForm.querySelector("input[name='contact_list_ids']");
    if (contactListIdsInput) { contactListIdsInput.value = contactListIds; }

    assignMultipleForm.submit();
  }

  // send_to_contacts_give_path is a `respond_to` action: Content-Type for the JSON body, plus
  // both the XHR and full Accept headers so it negotiates to `format.json`.
  onSendGifts(event) {
    event.preventDefault();

    this.buttonFooterTarget.querySelectorAll(".btn").forEach((btn) => btn.setAttribute("disabled", "disabled"));

    const onNextEvent = this.buttonFooterTarget.querySelector("#on_next_event");
    const params = { on_next_event: onNextEvent ? onNextEvent.checked : false };
    if (this.sendToIds.size > 0) {
      params.contact_ids = Array.from(this.sendToIds);
    } else {
      params.except_ids = Array.from(this.exceptIds);
    }

    const datatableFilters = this.tableTarget.getAttribute("data-source").split("?")[1];
    const url = `${event.currentTarget.dataset.url}?${datatableFilters}`;

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify(params);

    fetchJson(url, options).then(({ ok, data }) => {
      if (!ok) {
        this.buttonFooterTarget.querySelectorAll(".btn").forEach((btn) => btn.removeAttribute("disabled"));
        window.ltAlert({ message: "An error occurred, please try again later.", type: "toast-error" });
        return;
      }
      window.location = data.url;
    });
  }

  reload(extraParams = {}) {
    ProgressBar.start();

    // .datatable-moveable-filter/.datatable-filter are select2-initialized (initializers.js.erb,
    // owned by the datatable hub) - select2's own API (`.select2("data")`) and the change binding
    // below have to stay on jQuery; a native listener would never see select2's events.
    const $filterSelects = $(this.element).find(".datatable-moveable-filter, .datatable-filter");
    if ($filterSelects.length > 0) {
      const filter = {};
      $filterSelects.each((_, el) => {
        const $select = $(el);
        const key = $select.attr("name").replace(/(^.*\[|\].*$)/g, "");
        filter[key] = ($select.select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    this.element.querySelectorAll(".datatable-moveable-text-filter, .datatable-text-filter").forEach((el) => {
      extraParams["filter"] = extraParams["filter"] || {};
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value;
    });

    const params = Object.assign(this.currentParams(), extraParams);

    // R1 (skip): `data-source` - see the note in connect().
    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Bracket-notation query string into the nested object `$.param()` re-serializes; a
  // URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }

  // Fired by contact_lists_controller.js after a list is created/edited/deleted through its
  // modal. Subdomain::ContactsController#index `respond_to`s, so Accept is pinned to plain
  // "text/html" to force the html branch, alongside the usual X-Requested-With.
  reloadTopPanel() {
    const url = new URL(window.location.href);
    url.searchParams.set("partial", "true");

    const options = RequestHelper.defaultRequestOptions();
    options.headers = Object.assign({}, options.headers, {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "text/html",
    });

    fetch(url, options).then((response) => response.text()).then((html) => {
      // Same-origin, server-rendered by Subdomain::ContactsController#index
      // (partial: "index_top_panel", Haml, auto-escaped) - safe to inject directly.
      const topPanel = this.element.querySelector(".panel.top");
      if (topPanel) { topPanel.outerHTML = html; }
      this.setupContactListFilter();
      this.reload();
    });
  }
}
